// CLT (Cross Laminated Timber): ETA-11/0030 formulas (fh,k, distances) and stratigraphy (port of clt-layup.js),
// plus the CLT sub-menu logic of the form (currentClt/updateCltVisibility).

import { currentAlphaElem1, currentAlphaElem2, foldAcuteAngle, sin, cos } from "./angles";
import type { EngineCatalog } from "./catalog";
import { isSteel3, isSteelMat, isThreeEl, isPanel3, pInt, type EngineCtx } from "./ctx";
import { etaFhK, type ElementN } from "./materials";

export const CLT_LAYER_COUNTS = [3, 5, 6, 7, 8, 9, 11];
export const CLT_COUNTS_WITH_DISPOSIZIONE = [5, 7, 9, 11];
export const CLT_CLASSI = ["C14", "C16", "C18", "C20", "C22", "C24", "C27", "C30", "C35"];

export type CltStrato = {
  indice: number;
  orientamento: number; // 0 | 90
  doppio: boolean;
  spessore: number;
  rhok: number | null | undefined;
};
export type CltLayup = {
  latoInserimento: string; // narrow | wide
  numStrati: number;
  disposizione: string;
  strati: CltStrato[];
  spessoreTotale: number;
  classe: string;
  rhok: number | null;
};

// fh,k CLT (ETA-11/0030). NARROW side (screw parallel to the plane): 20·d^-0.5, independent of ε. WIDE side: "as for
// solid timber" with ρk = 110% of the minimum layer density (cltRhokWideFace) and the Hankinson reduction on the
// angle between force and grain of the OUTER layer: 1/(2.5·sin²α+cos²α). (Corrected 2026-10-08.)
export function etaFhKCltNarrow(d: number): number {
  return 20 * Math.pow(d, -0.5);
}
export function cltWideFhK(d: number, rho110: number, alphaDeg: number, predrilled: boolean): number {
  const a = Math.abs(alphaDeg);
  return etaFhK(d, rho110, 90, predrilled) / (2.5 * sin(a) * sin(a) + cos(a) * cos(a));
}

// 'userDefined' is always admitted; 'fisso' is the only automatic layup for 3/6/8 layers.
export function cltDisposizioniAmmesse(numStrati: number): string[] {
  if (!CLT_COUNTS_WITH_DISPOSIZIONE.includes(numStrati)) return ["fisso", "userDefined"];
  const opts = ["alternati", "accoppiati"];
  if (numStrati === 11) opts.push("zeroDoppio");
  opts.push("userDefined");
  return opts;
}
export function cltBuildOrientations(numStrati: number, disposizione: string | null | undefined): number[] {
  const ammesse = cltDisposizioniAmmesse(numStrati);
  const disp = disposizione == null ? ammesse[0] : disposizione;
  if (!CLT_COUNTS_WITH_DISPOSIZIONE.includes(numStrati)) {
    const seq: number[] = [];
    for (let i = 0; i < numStrati; i++) seq.push(i % 2 === 0 ? 0 : 90);
    if (numStrati % 2 === 0) {
      const half = numStrati / 2;
      const firstHalf = seq.slice(0, half);
      const secondHalf = firstHalf.slice().reverse();
      return firstHalf.concat(secondHalf);
    }
    return seq;
  }
  if (disp === "alternati" || disp === "zeroDoppio") {
    const seq: number[] = [];
    for (let i = 0; i < numStrati; i++) seq.push(i % 2 === 0 ? 0 : 90);
    return seq;
  }
  // 'accoppiati'
  const seq: number[] = new Array(numStrati).fill(null);
  seq[0] = 0;
  seq[1] = 0;
  seq[numStrati - 1] = 0;
  seq[numStrati - 2] = 0;
  let toggle = 90;
  for (let i = 2; i <= numStrati - 3; i++) {
    seq[i] = toggle;
    toggle = toggle === 90 ? 0 : 90;
  }
  return seq;
}
export function cltResolveThicknesses(
  orientations: number[],
  doppioFlags: boolean[],
  opts: { mode: string; spessore0: number; spessore90: number },
): number[] {
  const mode = opts.mode === "differenziato" ? "differenziato" : "uniforme";
  const s0 = opts.spessore0;
  const s90 = mode === "differenziato" ? opts.spessore90 : opts.spessore0;
  return orientations.map((o, i) => {
    const base = o === 0 ? s0 : s90;
    return doppioFlags[i] ? 2 * base : base;
  });
}

export type CltBuildOptions = {
  latoInserimento: string;
  numStrati: number;
  disposizione: string;
  spessoriMode: string;
  spessore0: number;
  spessore90: number;
  classe: string;
  userLayers?: { spessore: number; orientamento: number; classe: string }[];
};

// Layer classes are read from the species of 'Legno massiccio conifera' (C14-C35): the source names it 'Legno di
// conifere e di pioppo', here it comes in the user's language, so it is taken as the first one. Throws (like the
// source) on an invalid configuration: the callers degrade to "no CLT".
export function cltBuildClt(cat: EngineCatalog, o: CltBuildOptions): CltLayup {
  const numStrati = o.numStrati;
  if (!CLT_LAYER_COUNTS.includes(numStrati)) throw new RangeError(`numStrati non valido: ${numStrati}`);
  const richiedeDisposizione = CLT_COUNTS_WITH_DISPOSIZIONE.includes(numStrati);
  const disposizione = o.disposizione || (richiedeDisposizione ? "alternati" : "fisso");
  const classTable = Object.values(cat.TIMBER_DB["Legno massiccio conifera"] ?? {})[0];
  if (!classTable) throw new TypeError("classi legno massiccio conifera non disponibili");

  // 'userDefined': thickness/orientation/class of EACH layer (from the outside), each layer may have its own ρk.
  const ul = o.userLayers;
  if (disposizione === "userDefined" && Array.isArray(ul) && ul.length === numStrati) {
    const strati: CltStrato[] = ul.map((l, i) => {
      const classeL = l.classe || "C24";
      const props = classTable[classeL];
      return {
        indice: i,
        orientamento: l.orientamento === 90 ? 90 : 0,
        doppio: false,
        spessore: Math.max(l.spessore, 1) || 1,
        rhok: props ? props.rhok : undefined,
      };
    });
    const spessoreTotale = strati.reduce((a, b) => a + b.spessore, 0);
    const classiUniche = [...new Set(ul.map((l) => l.classe || "C24"))];
    const rhokValidi = strati.map((s) => s.rhok).filter((r): r is number => r != null);
    return {
      latoInserimento: o.latoInserimento,
      numStrati,
      disposizione,
      strati,
      spessoreTotale,
      classe: classiUniche.length === 1 ? (classiUniche[0] ?? "C24") : "mista: " + classiUniche.join("/"),
      rhok: rhokValidi.length ? Math.min(...rhokValidi) : null,
    };
  }

  const orientations = cltBuildOrientations(numStrati, disposizione);
  const doppioFlags = orientations.map((ang) => disposizione === "zeroDoppio" && ang === 0);
  const spessori = cltResolveThicknesses(orientations, doppioFlags, {
    mode: o.spessoriMode,
    spessore0: o.spessore0,
    spessore90: o.spessore90,
  });
  const classe = o.classe || "C24";
  const props = classTable[classe];
  if (!props) throw new TypeError(`classe CLT non valida: ${classe}`);
  const strati = orientations.map((orientamento, i) => ({
    indice: i,
    orientamento,
    doppio: !!doppioFlags[i],
    spessore: spessori[i] as number,
    rhok: props.rhok,
  }));
  const spessoreTotale = spessori.reduce((a, b) => a + b, 0);
  return { latoInserimento: o.latoInserimento, numStrati, disposizione, strati, spessoreTotale, classe, rhok: props.rhok };
}
export function cltRhokWideFace(clt: CltLayup): number {
  const rhokStrati = (clt.strati || []).map((s) => (s.rhok != null ? s.rhok : clt.rhok));
  const rhokMin = rhokStrati.length ? Math.min(...(rhokStrati as number[])) : (clt.rhok as number);
  return 1.1 * rhokMin;
}

// Acute angle between the force and the grain of the OUTER layer of element n's CLT panel.
export function cltOuterForceGrainAngle(ctx: EngineCtx, n: ElementN, clt: CltLayup | null): number {
  const cur = n === 1 ? currentAlphaElem1(ctx) : currentAlphaElem2(ctx);
  const outer = clt?.strati?.[0]?.orientamento || 0;
  return foldAcuteAngle(cur - outer);
}

// ---- CLT sub-menu of the form ------------------------------------------------------------------------------

function populateCltClasseOnce(ctx: EngineCtx, n: ElementN): void {
  ctx.options.cltClassi = CLT_CLASSI;
  const key = `clt${n}Classe` as const;
  if (!CLT_CLASSI.includes(ctx.v[key])) ctx.v[key] = "C24";
}
// Disposizione options admitted for the layer count (fallback to the first one); "userDefined" replaces
// spessoriMode/spessore/classe with the per-layer rows.
function updateCltDisposizioneVisibility(ctx: EngineCtx, n: ElementN): void {
  const numStrati = pInt(ctx.v[`clt${n}NumStrati` as const]);
  const ammesse = cltDisposizioniAmmesse(numStrati);
  ctx.options.cltDisposizione[n] = ammesse;
  const key = `clt${n}Disposizione` as const;
  if (!ammesse.includes(ctx.v[key])) ctx.v[key] = ammesse[0] ?? "fisso";
  const userDefined = ctx.v[key] === "userDefined";
  ctx.ui.visible[`clt${n}SpessoriModeField` as const] = !userDefined;
  ctx.ui.visible[`clt${n}Spessore0RowField` as const] = !userDefined;
  ctx.ui.visible[`clt${n}ClasseField` as const] = !userDefined;
  ctx.ui.visible[`clt${n}UserLayersWrap` as const] = userDefined;
  if (userDefined) renderCltUserLayers(ctx, n, numStrati);
}
// Per-layer rows: kept when their count matches numStrati, otherwise rebuilt reusing the previous values by index
// (defaults: 20 mm, alternating 0°/90°, C24).
function renderCltUserLayers(ctx: EngineCtx, n: ElementN, numStrati: number): void {
  const key = `clt${n}UserLayers` as const;
  const existing = ctx.v[key];
  if (existing.length === numStrati) return;
  const rows: { spessore: number; orientamento: number; classe: string }[] = [];
  for (let i = 0; i < numStrati; i++) {
    const p = existing[i];
    rows.push({
      spessore: p ? p.spessore : 20,
      orientamento: p ? p.orientamento : i % 2 === 0 ? 0 : 90,
      classe: p && CLT_CLASSI.includes(p.classe) ? p.classe : "C24",
    });
  }
  ctx.v[key] = rows;
}
function updateCltSpessoreVisibility(ctx: EngineCtx, n: ElementN): void {
  const differenziato = ctx.v[`clt${n}SpessoriMode` as const] === "differenziato";
  ctx.ui.visible[`clt${n}Spessore90Field` as const] = differenziato;
}

// Stratigraphy of element n (null when its material is not 'Pannelli CLT'). Throws on an invalid configuration.
export function currentClt(ctx: EngineCtx, n: ElementN): CltLayup | null {
  const v = ctx.v;
  const tipoSel = n === 1 ? v.mat1Tipo : v.mat2Tipo;
  const legnoAttivo = n === 2 || !isSteelMat(v.materiale1);
  if (!legnoAttivo || tipoSel !== "Pannelli CLT") return null;
  populateCltClasseOnce(ctx, n);
  updateCltDisposizioneVisibility(ctx, n);
  updateCltSpessoreVisibility(ctx, n);
  const disposizione = v[`clt${n}Disposizione` as const];
  let userLayers: CltBuildOptions["userLayers"];
  if (disposizione === "userDefined") {
    userLayers = v[`clt${n}UserLayers` as const].map((l) => ({
      spessore: l.spessore,
      orientamento: pInt(l.orientamento),
      classe: l.classe,
    }));
  }
  return cltBuildClt(ctx.cat, {
    latoInserimento: v[`clt${n}Lato` as const],
    numStrati: pInt(v[`clt${n}NumStrati` as const]),
    disposizione,
    spessoriMode: v[`clt${n}SpessoriMode` as const],
    spessore0: v[`clt${n}Spessore0` as const],
    spessore90: v[`clt${n}Spessore90` as const],
    classe: v[`clt${n}Classe` as const],
    userLayers,
  });
}
// `try { clt = currentClt(n); } catch (e) { clt = null; }` of the source.
export function tryCurrentClt(ctx: EngineCtx, n: ElementN): CltLayup | null {
  try {
    return currentClt(ctx, n);
  } catch {
    return null;
  }
}

// CLT sub-menu instead of the normal specie/classe/ρk fields; when active ρk{n} is disabled and filled with clt.rhok,
// t{n} with the total thickness. Element 1 (and element 2 in three-member / two-sided connections): only the wide
// side. Element 2 narrow: b2 = panel thickness and d2 = b2/2 (automatic), t2 (insertion depth) editable.
export function updateCltVisibility(ctx: EngineCtx, n: ElementN): void {
  const v = ctx.v,
    ui = ctx.ui;
  const tipoSel = n === 1 ? v.mat1Tipo : v.mat2Tipo;
  const legnoAttivo = n === 2 || !isSteelMat(v.materiale1);
  const active = legnoAttivo && tipoSel === "Pannelli CLT";
  ui.visible[`mat${n}NormalFields` as const] = !active;
  ui.visible[`mat${n}CltFields` as const] = active;
  if (active) v[`fi${n}` as const] = 0; // φ not considered with CLT
  if (n === 1) {
    ctx.options.cltLato[1] = active ? ["wide"] : ["narrow", "wide"];
    ui.disabled.clt1LatoNarrow = active;
    if (active && v.clt1Lato !== "wide") v.clt1Lato = "wide";
    ui.visible.clt1LatoHint = active;
  }
  const setAuto2 = (id: "b2" | "d2", val: number | null, auto: boolean): boolean => {
    ui.disabled[id] = auto;
    if (auto && val != null && v[id] !== val) {
      v[id] = val;
      return true;
    }
    return false;
  };
  if (n === 2) {
    const three2 = (isThreeEl(v.materiale1) || isSteel3(v.materiale1)) && active;
    ctx.options.cltLato[2] = three2 ? ["wide"] : ["narrow", "wide"];
    ui.disabled.clt2LatoNarrow = three2;
    if (three2 && v.clt2Lato !== "wide") v.clt2Lato = "wide";
    const wideOnly = ctx.t.engine.cltWideOnly;
    ui.texts.clt2LatoHint = three2
      ? isSteel3(v.materiale1)
        ? isPanel3(v.materiale1)
          ? wideOnly.panelSides
          : wideOnly.steelSides
        : wideOnly.three
      : ctx.t.clt.latoHint2;
    let narrow = false;
    let cltN: CltLayup | null = null;
    if (active) {
      cltN = tryCurrentClt(ctx, 2);
      narrow = !!(cltN && cltN.latoInserimento === "narrow");
    }
    ui.visible.clt2LatoHint = narrow || three2;
    ui.visible.cltFilaNote = narrow;
    setAuto2("b2", narrow ? cltN!.spessoreTotale : null, narrow);
    setAuto2("d2", narrow ? cltN!.spessoreTotale / 2 : null, narrow);
    if (narrow) {
      ui.disabled.rhok2 = true;
      v.rhok2 = cltN!.rhok != null ? cltN!.rhok : NaN;
      ui.disabled.spessore2 = false; // t2 editable (no longer the sum of the layers)
      return;
    }
  }
  if (active) {
    const clt = tryCurrentClt(ctx, n);
    ui.disabled[`rhok${n}` as const] = true;
    if (clt) v[`rhok${n}` as const] = clt.rhok != null ? clt.rhok : NaN;
    ui.disabled[`spessore${n}` as const] = true;
    if (clt) v[`spessore${n}` as const] = clt.spessoreTotale;
  } else {
    ui.disabled[`rhok${n}` as const] = false;
    ui.disabled[`spessore${n}` as const] = false;
  }
}
